import { Select, Stack } from '@mantine/core'
import { IconScoreboard } from '@tabler/icons-react'
import { z } from 'zod'
import { useCompetitionId } from '../../../../common/hooks'
import { Match, useMatches } from '../../../../matches/api'
import MatchScoreCard from '../../../../stories/MatchScoreCard/MatchScoreCard'
import { DivisionSelect, RoundSelect, TitleInput } from './OptionsFormFields'
import { WidgetEmpty, WidgetSection, WidgetSuspense } from './WidgetShell'
import { DashboardWidget } from './types'

const MAX_MATCHES = 3

const Schema = z.object({
  title: z.string().default('Matches'),
  division: z.number().nullable().default(null),
  round_mode: z
    .enum(['live', 'last_finished', 'custom'])
    .default('last_finished'),
  // Used only when round_mode === 'custom'.
  round_number: z.number().nullable().default(null),
})

type Settings = z.infer<typeof Schema>
type RoundMode = Settings['round_mode']

const ROUND_MODE_OPTIONS: { value: RoundMode; label: string }[] = [
  { value: 'live', label: 'Live round' },
  { value: 'last_finished', label: 'Last finished round' },
  { value: 'custom', label: 'Specific round' },
]

/** Highest round number among matches whose round has the given status. */
function latestRoundNumber(
  matches: Match[],
  status: Match['round_status']
): number | null {
  const nums = matches
    .filter((m) => m.round_status === status)
    .map((m) => m.round_number)
  return nums.length ? Math.max(...nums) : null
}

/** Resolve which round the widget should display, or null if none applies. */
function pickRoundNumber(matches: Match[], settings: Settings): number | null {
  if (settings.round_mode === 'custom') {
    return settings.round_number
  }
  if (settings.round_mode === 'live') {
    const live = latestRoundNumber(matches, 'live')
    if (live != null) return live
    // No live round — fall back to the last finished round.
  }
  return latestRoundNumber(matches, 'ended')
}

function pickRoundMatches(matches: Match[], settings: Settings): Match[] {
  const roundNumber = pickRoundNumber(matches, settings)
  if (roundNumber == null) return []
  return matches
    .filter((m) => m.round_number === roundNumber)
    .sort((a, b) => a.match_id - b.match_id)
    .slice(0, MAX_MATCHES)
}

function Inner({ settings }: { settings: Settings }) {
  const competition_id = useCompetitionId()
  const { data: matches } = useMatches({
    competition_id,
    division: settings.division ?? undefined,
  })
  const selected = pickRoundMatches(matches, settings)

  if (selected.length === 0) {
    const message =
      settings.round_mode === 'custom' && settings.round_number == null
        ? 'Pick a round'
        : 'No matches yet'
    return <WidgetEmpty message={message} />
  }

  return (
    <Stack gap="md" h="100%" w="100%" style={{ minHeight: 0 }}>
      {selected.map((m) => (
        <div key={m.match_id} style={{ flex: 1, minHeight: 0 }}>
          <MatchScoreCard match={m} />
        </div>
      ))}
    </Stack>
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSection title={settings.title} boxed={false}>
      <WidgetSuspense>
        <Inner settings={settings} />
      </WidgetSuspense>
    </WidgetSection>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <DivisionSelect
        value={value.division}
        onChange={(v) => patch({ division: v })}
      />
      <Select
        label="Round"
        data={ROUND_MODE_OPTIONS}
        value={value.round_mode}
        onChange={(v) =>
          patch({ round_mode: (v as RoundMode) || 'last_finished' })
        }
      />
      {value.round_mode === 'custom' && (
        <RoundSelect
          value={value.round_number}
          onChange={(v) => patch({ round_number: v })}
        />
      )}
    </Stack>
  )
}

export const MatchesWidget: DashboardWidget<Settings> = {
  type: 'matches',
  label: 'Matches',
  description: 'Match results for a selected round',
  icon: IconScoreboard,
  minW: 2,
  minH: 3,
  maxW: 2,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  Component,
  OptionsForm,
}
